import {
  InfiniteApplicationApplicant,
  getInfiniteApplication,
  getLuxLoyaltyAccount,
  getLuxLoyaltyAccountByNumber,
} from '~/services/LuxLoyalty';
import UsersService from '~/services/UsersService';

import { getDisplayName, getPhone } from './LuxLoyaltyInfiniteProfileDetails.utils';

export type InfiniteLinkedMemberRole = 'PRIMARY' | 'SECONDARY';

export type InfiniteLinkedMember = {
  customerId: string | null;
  name: string;
  email: string;
  phone: string;
  relationship?: string | null;
  role: InfiniteLinkedMemberRole;
};

type UserSearchSummary = {
  id_member?: string;
  email?: string;
};

type LinkedPerson = {
  title?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phonePrefix: string;
  phone: string;
  relationship?: string | null;
};

export function mapLinkedMember(
  person: LinkedPerson,
  role: InfiniteLinkedMemberRole,
  customerId: string | null,
): InfiniteLinkedMember {
  return {
    customerId,
    name: getDisplayName(person),
    email: person.email,
    phone: getPhone(person),
    relationship: person.relationship ?? null,
    role,
  };
}

export function mapLinkedMemberFromAccount(
  account: App.LuxLoyaltyAccount,
  role: InfiniteLinkedMemberRole,
  fallback?: LinkedPerson,
): InfiniteLinkedMember | null {
  const profile = account.infiniteProfile;
  if (!profile && !fallback) {
    return null;
  }

  if (profile) {
    return mapLinkedMember(profile, role, account.customerId || profile.customerId || null);
  }

  return mapLinkedMember(fallback as LinkedPerson, role, account.customerId || null);
}

export function asUserSearchSummaries(response: unknown): Array<UserSearchSummary> {
  if (Array.isArray(response)) {
    return response;
  }

  if (response && typeof response === 'object' && Array.isArray((response as { result?: unknown }).result)) {
    return (response as { result: Array<UserSearchSummary> }).result;
  }

  return [];
}

async function findCustomerIdByEmail(email: string, brand: string, signal?: AbortSignal): Promise<string | null> {
  const normalisedEmail = email.trim().toLowerCase();
  if (!normalisedEmail) {
    return null;
  }

  const users = asUserSearchSummaries(
    await UsersService.getUsers(
      {
        filter: email,
        limit: 10,
        offset: 0,
        brand,
      },
      { signal },
    ),
  );

  return users.find((user) => user.email?.toLowerCase() === normalisedEmail)?.id_member ?? null;
}

async function safeGetLuxLoyaltyAccount(customerId: string): Promise<App.LuxLoyaltyAccount | null> {
  try {
    return await getLuxLoyaltyAccount(customerId);
  } catch {
    return null;
  }
}

export async function fetchPrimaryLinkedMember(primaryAccountNumber: number): Promise<InfiniteLinkedMember | null> {
  const account = await getLuxLoyaltyAccountByNumber(primaryAccountNumber);
  if (!account) {
    return null;
  }

  return mapLinkedMemberFromAccount(account, 'PRIMARY');
}

export async function fetchSecondaryLinkedMembers(
  applicationId: string,
  brand: string,
  signal?: AbortSignal,
): Promise<Array<InfiniteLinkedMember>> {
  const application = await getInfiniteApplication(applicationId);
  const applicants = application.result?.applicants ?? [];
  const secondaryApplicants = applicants.filter(
    (applicant: InfiniteApplicationApplicant) => applicant.role === 'SECONDARY',
  );

  return Promise.all(
    secondaryApplicants.map(async (applicant) => {
      const customerId = await findCustomerIdByEmail(applicant.email, brand, signal);
      const account = customerId ? await safeGetLuxLoyaltyAccount(customerId) : null;

      if (account) {
        return (
          mapLinkedMemberFromAccount(account, 'SECONDARY', applicant) ??
          mapLinkedMember(applicant, 'SECONDARY', customerId)
        );
      }

      return mapLinkedMember(applicant, 'SECONDARY', customerId);
    }),
  );
}
